import ContentLayout from "@/components/layout/ContentLayout";
import OverviewAgenda from "@/components/overview-agenda";
import MobileLandscapeOverview from "@/components/mobile-landscape-overview";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CalendarEvent } from "@/components/ui/full-calendar";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTE } from "@/routes/constants";
import {
  getAssignmentListByStudent,
  querySurveyV2,
} from "@/services/generatedAPIs";
import { AssignmentModel, GetAssignmentListResponse } from "@/services/generatedAPIs.schemas";
import { addHours, differenceInDays, format } from "date-fns";
import { ReactNode, useEffect, useState, useRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { useSubjectList } from "@/hooks/use-subject-list";
import { getSubjectDisplayName } from "@/hooks/use-subject-name";
import styles from "@/components/css/OverviewPage.module.css";
import { isEN } from "@/utils/i18n";

import Survey from "@/components/survey"; // 17 items
import Survey2 from "@/components/survey2"; // 6 items
import Survey3 from "@/components/survey3"; // 10 items
import Survey4 from "@/components/survey4"; // 12 items
import Survey5 from "@/components/survey5";

const OVERVIEW_DEFAULT_ACCENT = "#9CEA4F";

type OverviewTheme = "orange" | "green" | "blue" | "pink";

function readOverviewTheme(): OverviewTheme {
  if (typeof document === "undefined") return "orange";
  const t = document.documentElement.getAttribute("data-theme") as OverviewTheme | null;
  return t && (["orange", "green", "blue", "pink"] as const).includes(t) ? t : "orange";
}

function useOverviewAccent(): { accent: string; panelBg: string; arrow: string; arrowFg: string } {
  const [theme, setTheme] = useState<OverviewTheme>(readOverviewTheme);
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(readOverviewTheme()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  if (theme === "orange") {
    return {
      accent: OVERVIEW_DEFAULT_ACCENT,
      panelBg: "#D0FFCD",
      // 課業輪播左右箭頭：默認主題綠底 + 黑色箭頭符號
      arrow: "#7DD02A",
      arrowFg: "#1A1C1D",
    };
  }
  // 其餘主題：底色跟隨 primary 主題色，箭頭符號改白色（深底上黑箭頭辨識度不足）
  return { accent: "var(--primary)", panelBg: "#FEFEFE", arrow: "var(--primary)", arrowFg: "#FFFFFF" };
}

function ProgressRing({
  percent,
  color,
  size = 84,
  stroke = 9,
}: {
  percent: number;
  color: string;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  // 100% 时圆头端点会在起点/终点交叠处挤出一条白缝；改为闭合整圆 + 方头避免缺口。
  const isFull = clamped >= 99.5;
  const offset = isFull ? 0 : circumference - (clamped / 100) * circumference;
  return (
    <div className="relative h-full w-full">
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E6E6E6" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap={isFull ? "butt" : "round"}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {/* 环形中心数值使用深色，与进度弧描边分层，确保对比度与可读性 */}
        <span className="text-sm font-bold tabular-nums text-[#1A1C1D] xl:text-base 2xl:text-lg">{clamped}%</span>
      </div>
    </div>
  );
}

type CarouselDataType = {
  /** 題型序號（全局連續，作卡片左上角小標籤） */
  ordinal: number;
  /** 題型名稱（來自作業 topicList，粗體顯示） */
  title: string;
  /** 該題型已完成題數 / 總題數 → 環形完成進度 */
  done: number;
  total: number;
  assignments: AssignmentModel[];
};

function formatOrdinal(n: number, lang: string): string {
  if (lang.startsWith('en')) {
    // 11/12/13（含 111/112/113…）一律 th，其余按个位取 st/nd/rd
    const mod100 = n % 100;
    const suffix =
      mod100 >= 11 && mod100 <= 13
        ? 'th'
        : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
    return `${n}${suffix}`;
  }
  return String(n).padStart(2, '0');
}

function OverviewPage({ t, i18n, ...props }: WithTranslation) {
  const { getCurrentUser } = useAuth();
  const { accent, panelBg, arrow, arrowFg } = useOverviewAccent();
  const { subjectInfos } = useSubjectList();
  const [carouselData, setCarouselData] = useState<CarouselDataType[]>([]);
  const [calendarData, setCalendarData] = useState<CalendarEvent[]>([]);
  const [surveyIndex, setSurveyIndex] = useState<number | undefined>();
  const [carApi, setCarApi] = useState<CarouselApi>();
  const [assignmentRaw, setAssignmentRaw] = useState<GetAssignmentListResponse | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Prevent double fetching in StrictMode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    getAssignmentListByStudent({ studentId: getCurrentUser()!.id, type: "homework" })
      .then((rsp) => {
        if (rsp.code === 200 && rsp.data) {
          setAssignmentRaw(rsp.data);
        }
      })
      .catch((e) => {
        console.error("Error fetching assignment list", e);
      });
  }, []);

  useEffect(() => {
    if (!assignmentRaw) return;

    const assignmentList = assignmentRaw.assignmentList;
    const statsList = assignmentRaw.assignmentStatsList;
    const cards: CarouselDataType[] = [];
    const newCalendarData: CalendarEvent[] = [];
    let ordinal = 0;

    for (let i = 0; i < assignmentList.length; ++i) {
      const assignment = assignmentList[i];
      const fallbackName = assignment.subject || t("common.loading");
      const subjectName = getSubjectDisplayName(
        subjectInfos,
        assignment.subjectId,
        fallbackName,
        i18n.language
      ) ?? fallbackName;
      const stat = statsList[i];

      // 方案B: dev-cpp 卡片模型 —— 逐作业一张卡(连续题型序号), 不排除已完成, 无随心练占位卡
      cards.push({
        ordinal: ++ordinal,
        title: assignment.assignment.title || assignment.topicList.join("、") || subjectName,
        done: stat?.submittedCount ?? 0,
        total: stat?.totalCount ?? 0,
        assignments: [assignment],
      });

      // Add all assignments to calendar (both completed and incomplete)
      const dueDate = new Date(assignment.assignment.dueDate!);
      newCalendarData.push({
        id: i.toString(),
        start: dueDate,
        end: addHours(dueDate, 1),
        title: subjectName + ":" + assignment.topicList.join(","),
        data: assignmentList[i],
        color: "pink",
      });
    }

    setCarouselData(cards);
    setCalendarData(newCalendarData);
  }, [assignmentRaw, subjectInfos, i18n, t]);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    const setup = async () => {
      const userId = getCurrentUser()!.id;

      let rsp = await querySurveyV2({ userId, surveyId: 1 });
      if (rsp.code === 200 && rsp.data!.hasRecord === false) {
        setSurveyIndex(1);
        return;
      }

      rsp = await querySurveyV2({ userId, surveyId: 2 });
      if (rsp.code === 200 && rsp.data!.hasRecord === false) {
        setSurveyIndex(2);
        return;
      }
      if (!(rsp.code === 200 && (differenceInDays(new Date().toISOString(), (rsp.data!.submitTime! + 'Z')) > 1) || (getCurrentUser()!.schoolName === "C.C.C. Mong Wong Far Yok Memorial Primary School"))) {
        return;
      }

      rsp = await querySurveyV2({ userId, surveyId: 3 });
      if (rsp.code === 200 && rsp.data!.hasRecord === false) {
        setSurveyIndex(3);
        return;
      }

      rsp = await querySurveyV2({ userId, surveyId: 4 });
      if (rsp.code === 200 && rsp.data!.hasRecord === false) {
        setSurveyIndex(4);
        return;
      }
      if (!(rsp.code === 200 && differenceInDays(new Date().toISOString(), (rsp.data!.submitTime! + 'Z')) > 7)) {
        return;
      }

      rsp = await querySurveyV2({ userId, surveyId: 5 });
      if (rsp.code === 200 && rsp.data!.hasRecord === false) {
        setSurveyIndex(5);
      }
    }
    setup();
  }, [getCurrentUser]);

  return (
    <>
    {/* 手机横屏(高≤500)隐藏桌面/平板版,改走独立 844×390 FitScale 舞台(见文件尾 MobileLandscapeOverview) */}
    <ContentLayout title={t("student.overview")} className="md:h-full md:overflow-hidden [@media(max-height:500px)]:hidden">
      {/* lg/xl 左 padding 对齐 2xl 的 pl-0(贴近侧栏,gap≈24)；右 padding 与 ContentLayout 顶栏保持一致(pr-10)，
          使右上角控制区/数据卡与下方课业、日程表面板统一右对齐。pb-6 恒定=侧栏 m-6 使日程表底缘对齐,矮屏(max-h:1000)只收 gap/pt 不收 pb(pb-3 会低 12px) */}
      <div
        className="flex flex-col gap-6 overflow-visible px-6 pb-6 pt-6 lg:pl-0 lg:pr-10 2xl:pl-0 2xl:pr-10 min-[1920px]:pl-0! min-[1920px]:pr-10! md:flex-1 md:min-h-0 md:overflow-y-auto [@media(max-height:1000px)]:gap-3! [@media(max-height:1000px)]:pt-3!"
      >
        <section
          className="overflow-visible rounded-[50px] p-6 lg:p-8 2xl:min-h-[400px] 2xl:p-10 [@media(max-height:1000px)]:shrink-0 [@media(max-height:1000px)]:rounded-[28px]! [@media(max-height:1000px)]:p-4!"
          style={{ background: panelBg }}
        >
          {/* 面板標題：數學符號 glyph + 課業 */}
          <div className="mb-6 flex items-center gap-3 [@media(max-height:1000px)]:mb-2! [@media(max-height:1000px)]:gap-2!">
            <span
              className="grid grid-cols-2 gap-x-1 text-[20px] font-extrabold leading-none text-[#1A1C1D] [@media(max-height:1000px)]:text-base!"
              aria-hidden
            >
              <span>+</span>
              <span>−</span>
              <span>×</span>
              <span>÷</span>
            </span>
            <h2 className="text-2xl font-bold text-foreground md:text-3xl lg:text-4xl 2xl:text-[40px] [@media(max-height:1000px)]:text-2xl!">
              {t("common.mathsHomework")}
            </h2>
          </div>

          <div className="relative px-10 2xl:px-14">
            {carouselData.length > 0 && (
              <button
                type="button"
                onClick={() => carApi?.scrollPrev()}
                aria-label={t('common.previous', { defaultValue: '上一頁' })}
                className="absolute left-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full shadow-sm transition hover:brightness-95 2xl:h-12 2xl:w-12"
                style={{ backgroundColor: arrow, color: arrowFg, padding: 8 }}
              >
                <ChevronLeft className="size-6 2xl:size-7" strokeWidth={2.5} />
              </button>
            )}
            {carouselData.length > 0 && (
              <button
                type="button"
                onClick={() => carApi?.scrollNext()}
                aria-label={t('common.next', { defaultValue: '下一頁' })}
                className="absolute right-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full shadow-sm transition hover:brightness-95 2xl:h-12 2xl:w-12"
                style={{ backgroundColor: arrow, color: arrowFg, padding: 8 }}
              >
                <ChevronRight className="size-6 2xl:size-7" strokeWidth={2.5} />
              </button>
            )}

            <Carousel
              opts={{
                align: "start",
              }}
              setApi={setCarApi}
              className={`w-full ${styles.carouselOverflowVisible}`}
            >
              <CarouselContent className={styles.carouselOverflowVisible}>
                {carouselData.map((data: CarouselDataType, index: number) => {
                const percent =
                  data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;
                return (
                  <CarouselItem key={index} className="w-fit basis-auto pl-4 [@media(min-height:1200px)_and_(max-width:1279px)]:basis-1/3! [@media(min-height:1200px)_and_(max-width:1279px)]:w-auto!">
                    <NavLink to={ROUTE.STUDENT.HOMEWORK} state={data.assignments}>
                      <Card className="flex aspect-[204/235] w-[clamp(160px,calc(5.625vw+96px),240px)] flex-col rounded-[30px] border-0 bg-white px-[13%] pt-[13%] shadow-[0_8px_24px_-14px_rgba(0,0,0,0.25)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-14px_rgba(0,0,0,0.3)] [@media(max-height:1000px)]:w-[clamp(140px,16vw,172px)]! [@media(max-height:1000px)]:rounded-[22px]! [@media(min-height:1200px)_and_(max-width:1279px)]:w-full!">
                        <CardContent className="flex h-full flex-col p-0">
                          {/* TOP: 題型序號 label + 題型名稱 */}
                          <div className="min-w-0 shrink-0">
                            {/* 題型序號 label — slate-400，1920 下 16px */}
                            <div className="line-clamp-1 text-[14px] font-medium leading-none text-[#94A3B8] 2xl:text-[16px] [@media(max-height:1000px)]:text-[12px]!">
                              {t("student.questype")} {formatOrdinal(data.ordinal, i18n.language)}
                            </div>
                            {/* 題型名稱 — bold，1920 下 24px；与 label 间距 ~17px(11%)；固定预留两行高度，超出两行省略并显示完整文本提示 */}
                            <div
                              title={data.title}
                              className="mt-[11%] line-clamp-2 min-h-[2lh] break-words text-[20px] font-bold leading-tight text-[#1A1C1D] xl:text-[22px] min-[1920px]:text-[24px]! [@media(max-height:1000px)]:mt-[6%]!"
                            >
                              {data.title}
                            </div>
                          </div>

                          {/* progress ring — 与名称间距统一 14%；直径为内容区宽度 50%，随卡片宽度缩放 */}
                          <div className="mt-[14%] aspect-square w-[50%]">
                            <ProgressRing percent={percent} color={accent} />
                          </div>
                        </CardContent>
                      </Card>
                    </NavLink>
                  </CarouselItem>
                );
              })}
              </CarouselContent>
            </Carousel>
          </div>
        </section>

        <OverviewAgenda
          events={calendarData}
          accent={accent}
          arrow={arrow}
          isEnglish={isEN(i18n)}
          calendarLabel={t("common.calendar")}
          confirmLabel={t("component.confirm")}
          clearLabel={t("selfAssess.clearFilter")}
          // md+ 撑到剩余高度: 与课业面板组成 flex-col, 日程表底部与左侧菜单栏底部对齐
          // iPad 直向(高≥1200 且宽≤1279)改为按内容高度，避免两周日历被拉成整屏 (ZH-12/EN-04)
          className="md:flex-1 md:min-h-0 [@media(min-height:1200px)_and_(max-width:1279px)]:flex-none!"
          renderTooltip={(event): ReactNode => {
            const model = event.data as AssignmentModel | undefined;
            const createdAt = model?.assignment.createdAt;
            const dueDate = model?.assignment.dueDate;
            return (
              <div className="flex flex-col gap-1.5 text-sm">
                <span className="font-semibold text-[#1A1C1D]">
                  {event.title}
                </span>
                {createdAt && (
                  <span className="inline-flex items-center gap-x-1.5 text-[#64748B]">
                    <span>{t("common.publish")}:</span>
                    <span className="tabular-nums tracking-wide">{format(new Date(createdAt), "yyyy/MM/dd")}</span>
                  </span>
                )}
                {dueDate && (
                  <span className="inline-flex items-center gap-x-1.5 text-[#64748B]">
                    <span>{t("common.due")}:</span>
                    <span className="tabular-nums tracking-wide">{format(new Date(dueDate), "yyyy/MM/dd")}</span>
                  </span>
                )}
                {model && (
                  <NavLink
                    to={ROUTE.STUDENT.HOMEWORK}
                    state={[model]}
                    className="mt-0.5 font-semibold text-[color:var(--sidebar-active)] hover:underline"
                  >
                    {t("student.goreview")}
                  </NavLink>
                )}
              </div>
            );
          }}
        />
      </div>

      {surveyIndex === 1 && (
        <Survey
          onSuccess={() => {
            setSurveyIndex(2);
          }}
        ></Survey>

      )}

      {surveyIndex === 2 && (
        <Survey2 onSuccess={() => {
          if (getCurrentUser()!.schoolName === "C.C.C. Mong Wong Far Yok Memorial Primary School") {
            setSurveyIndex(3);
          } else {
            setSurveyIndex(undefined);
          }
        }}></Survey2>
      )}

      {surveyIndex === 3 && (
        <Survey3
          onSuccess={() => {
            setSurveyIndex(4);
          }}
        ></Survey3>
      )}

      {surveyIndex === 4 && (
        <Survey4 onSuccess={() => {
          setSurveyIndex(undefined);
        }}></Survey4>
      )}

      {surveyIndex === 5 && (
        <Survey5 onSuccess={() => {
          setSurveyIndex(undefined);
        }}></Survey5>
      )}
    </ContentLayout>

    <MobileLandscapeOverview carouselData={carouselData} calendarData={calendarData} />
    </>
  );
}

export default withTranslation()(OverviewPage);
